'use client';

import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useMemo } from 'react';
import { getPriceValueTranslation } from '@/entities/cart';
import { OrderNumber } from '@/entities/order/ui/order-number';
import { QueryApi } from '@/features/query/api';
import { formatAddress } from '@/shared/lib/formatAddress';
import { Currency } from '@/shared/model/Currency';
import { PriceValue } from '@/shared/model/PriceValue';
import { Icon } from '@/shared/ui/icon';
import { ModalService } from '@/shared/ui/modal/ModalService';
import './orders-history-modal.scss';

type CustomerOrder = NonNullable<
    Awaited<ReturnType<NonNullable<ReturnType<typeof QueryApi.Customer.queries.Orders>['queryFn']>>>
>['orders'][number];

type Props = {
    orderId: string;
    orderNumber: string;
    dateTime: string;
    address: CustomerOrder['address'];
    priceValue: PriceValue;
};
export const OrdersHistoryModalOrder = ({ orderId, orderNumber, dateTime, address, priceValue }: Props) => {
    const t = useTranslations();
    const locale = useLocale();
    const formattedAddress = useMemo(() => formatAddress(address), [address]);
    const formattedDateTime = new Date(dateTime).toLocaleString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
    });

    const showOrderModal = () => {
        ModalService.showPrefab('OrderModal', { orderId });
    };

    return (
        <button
            className="orders-history-modal__order"
            type="button"
            data-testid="orders-history-modal-order"
            onClick={showOrderModal}
        >
            <OrderNumber orderNumber={orderNumber} data-testid="orders-history-modal-order-number" />
            <div className="orders-history-modal__order-content">
                <div className="orders-history-modal__order-info">
                    <span
                        className="orders-history-modal__order-datetime"
                        data-testid="orders-history-modal-order-datetime"
                    >
                        {formattedDateTime}
                    </span>
                    <span
                        className="orders-history-modal__order-address"
                        data-testid="orders-history-modal-order-address"
                    >
                        {formattedAddress}
                    </span>
                </div>
                <span
                    className="orders-history-modal__order-price"
                    data-testid="orders-history-modal-order-price"
                >
                    {getPriceValueTranslation({
                        amount: Number(priceValue.amount),
                        currency: priceValue.currency as Currency,
                        t,
                    })}
                </span>
                <Icon name="chevron-right" className="orders-history-modal__order-chevron" />
            </div>
        </button>
    );
};
